import { brand, useBrandFeatures, useBrandLogo, useSiteSettings } from '@/brands';
import { Icons } from '@/utils/Icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import UserDropdown from './UserDropdown';
import Navbar from '@/components/Navbar';
import HeaderTopGames from './HeaderTopGames';
import MobileNavbar from '@/components/MobileNavbar';
import { useState } from 'react';
import SetButtonValueModal from './SetButtonValueModal';
import RulesModal from '@/components/RulesModal';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { setAgeModalOpen, setRulesModalOpen } from '@/store/slices/uiSlice';
import AgeRestrictionModal from '@/components/AgeRestrictionModal';
import Marquee from '@/components/Marquee';
import { useGetUserInfoQuery, useGetUserWalletsQuery } from '@/store/api/userApi';
import SearchDropdown from '@/components/common/SearchDropdown';
import { formatNumber } from '@/utils';
import MyMarketModal from './MyMarketModal';
import { FaBookOpen, FaPlusCircle, FaMoneyBillWave, FaPiggyBank } from 'react-icons/fa';

const Header = ({ onMenuClick }: { onMenuClick: () => void }) => {
  const logo = useBrandLogo();
  const [showBalance, setShowBalance] = useState(true);
  const [showExposure, setShowExposure] = useState(true);
  const [showMyMarketModal, setShowMyMarketModal] = useState(false);
  const location = useLocation();
  const reportsPage = location.pathname.includes('user');
  const isHomePage = location.pathname.includes('home');
  // CRASH page only: show header up to the search row (hide the bottom nav)
  const isCrashPage = location.pathname.toUpperCase().includes('CRASH');
  // Game details (markets) page: also show header only up to the search row
  const isGameDetailsPage = location.pathname.includes('game-details');
  // qtech pages: hide the popular-events (HeaderTopGames) row
  const isQtechPage = location.pathname.includes('qtech');
  // fantasy / live-casino lobbies: also hide the popular-events (HeaderTopGames) row
  const isFantasyPage = location.pathname.includes('fantasy-list');
  const isLiveCasinoPage = location.pathname.includes('live-casino');
  const isSlotsPage = location.pathname.includes('slots');
  // casino pages (e.g. /casino/all): hide the popular-events (HeaderTopGames) row
  const isCasinoPage = location.pathname.includes('casino');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { ageModalOpen } = useAppSelector((state) => state.ui);
  // Cashier buttons are a per-brand switch; the routes behind them are unmounted
  // in App.tsx when it is off, so hiding these keeps the header from dead-ending.
  const { deposit: cashierOn } = useSiteSettings();
  // The Site Setting above is one switch for the whole cashier; the super
  // admin's Features split it per direction, so a brand can take deposits while
  // withdrawals are paused (or run FD without either). Both have to pass, and
  // the router refuses the URLs behind any button this drops.
  const { deposit: depositOn, withdrawal: withdrawalOn, fd: fdOn } = useBrandFeatures();
  const showDeposit  = cashierOn && depositOn;
  const showWithdraw = cashierOn && withdrawalOn;
  const showFd       = cashierOn && fdOn;

  useGetUserInfoQuery(undefined, { pollingInterval: 5000 });
  useGetUserWalletsQuery(undefined, { pollingInterval: 5000 });

  const { info, walletInfo } = useAppSelector((state) => state.user);
  const username = info?.username ?? 'Demo';
  const isDemoUser = info?.role?.name?.toLowerCase() == 'demo';
  const mainWallet = walletInfo?.find((item) => item?.type == 'Main');
  const mainBalance =
    Number(mainWallet?.amount ?? 0) -
    Math.abs(Number(mainWallet?.exposureAmount ?? 0)) -
    Number(mainWallet?.lockedAmount ?? 0);
  const exposure = mainWallet?.exposureAmount;

  return (
    <>
      <style>{`
        .hdr {
          /* brand primary — set by applyBrandTheme() */
          background: var(--brand-primary, #0088cc);
          position: sticky;
          top: 0;
          z-index: 50;
          box-shadow: 0 3px 14px rgba(0,0,0,0.25);
          overflow-x: hidden;
          width: 100%;
          box-sizing: border-box;
        }
        .hdr *, .hdr *::before, .hdr *::after { box-sizing: border-box; }

        /* ROW 1 */
        .hdr-r1 {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 6px;
          padding: 8px 12px;
          width: 100%;
        }

        /* LEFT */
        .hdr-left {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }
        .hdr-ibtn {
          /* open icon — no box */
          padding: 0;
          background: transparent;
          border: none;
          display: flex; align-items: center; justify-content: center;
          color: #fff;
          cursor: pointer;
          flex-shrink: 0;
          transition: transform .15s, opacity .15s;
        }
        .hdr-ibtn:hover { opacity: 0.85; }
        .hdr-ibtn:active { transform: scale(0.9); }
        .hdr-logo img { width: auto; height: 48px; object-fit: contain; display: block; }

        /* CENTER — desktop only */
        .hdr-center { display: none; }

        /* RIGHT */
        .hdr-right {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 1;
          min-width: 0;
        }

        /* Account block — Balance on top, Exp + Demo below (reference) */
        .hdr-acct {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
          min-width: 0;
        }
        .hdr-bal {
          color: #fff;
          font-size: 13px;
          font-weight: 700;
          line-height: 1.15;
          white-space: nowrap;
        }
        .hdr-bal b { font-weight: 800; margin-left: 2px; }
        .hdr-exp-row {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .hdr-exp {
          color: #fff;
          font-size: 13px;
          font-weight: 600;
          line-height: 1.15;
          white-space: nowrap;
        }
        .hdr-exp b { font-weight: 800; margin-left: 2px; }

        /* Rules + Deposit/Withdraw btns — hidden mobile */
        .hdr-rules { display: none; }
        .hdr-dw { display: none; }

        /* ROW 2 — mobile search + marquee */
        .hdr-r2 {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0 12px 8px;
          width: 100%;
        }
        .hdr-r2-search { flex: 0 0 auto; min-width: 0; }   /* just the search icon (expands on tap) */
        .hdr-r2-marq {
          flex: 1;                                          /* marquee fills the rest (~377px) */
          min-width: 0;
          height: 30px;
          display: flex;
          align-items: center;
          overflow: hidden;
          background: rgba(255,255,255,0.14);
          border-radius: 6px;
          padding: 2px 8px;
        }

        /* Mobile Deposit/Withdraw quick row (hidden on desktop) */
        .hdr-dwm {
          display: flex;
          gap: 8px;
          padding: 0 12px 8px;
          width: 100%;
        }
        .hdr-dwm-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 9px 8px;
          border-radius: 8px;
          border: none;
          color: #fff;
          font-size: 13px;
          font-weight: 800;
          cursor: pointer;
          transition: filter .15s, transform .1s;
        }
        .hdr-dwm-btn:active { transform: scale(0.97); }
        /* Colours come from the brand (superadmin → theme.buttons); fallbacks match DEFAULT_ACTION_BUTTONS. */
        .hdr-dwm-btn.dep { background: linear-gradient(180deg, var(--btn-dep-from,#5a9bf2), var(--btn-dep-to,#3d7fe0)); box-shadow: 0 2px 6px rgba(0,0,0,0.28); }
        .hdr-dwm-btn.wd  { background: linear-gradient(180deg, var(--btn-wd-from,#f78da2),  var(--btn-wd-to,#ef6885));  box-shadow: 0 2px 6px rgba(0,0,0,0.28); }
        .hdr-dwm-btn.fd  { background: linear-gradient(180deg, var(--btn-fd-from,#5a9bf2),  var(--btn-fd-to,#3d7fe0));  box-shadow: 0 2px 6px rgba(0,0,0,0.28); }

        /* BOTTOM */
        .hdr-bot { border-top: 1px solid rgba(0,0,0,0.12); }

        /* ── DESKTOP ≥1280px ── */
        @media (min-width: 1280px) {
          .hdr-r1     { padding: 12px 24px; gap: 14px; }
          .hdr-logo img { width: auto; height: 52px; }
          .hdr-ibtn   { display: none; }
          .hdr-center { display: flex; align-items: center; gap: 14px; flex: 1; min-width: 0; }
          .hdr-r2     { display: none; }
          .hdr-dwm    { display: none; }
          .hdr-right  { gap: 14px; }
          .hdr-bal, .hdr-exp { font-size: 14px; }
          .hdr-rules  {
            display: flex; align-items: center; gap: 5px;
            padding: 6px 13px; border-radius: 10px;
            font-size: 12px; font-weight: 700;
            background: rgba(255,255,255,0.14);
            border: 1px solid rgba(255,255,255,0.24);
            color: #fff;
            cursor: pointer; white-space: nowrap;
            transition: background .2s;
          }
          .hdr-rules:hover { background: rgba(255,255,255,0.26); }

          /* Deposit / Withdraw buttons near the balance */
          .hdr-dw {
            display: flex; align-items: center; gap: 5px;
            padding: 6px 14px; border-radius: 10px;
            font-size: 12px; font-weight: 800;
            color: #fff; border: none; cursor: pointer; white-space: nowrap;
            transition: filter .2s, transform .1s;
          }
          .hdr-dw:hover  { filter: brightness(1.08); }
          .hdr-dw:active { transform: scale(0.96); }
          .hdr-dw.dep { background: linear-gradient(180deg, var(--btn-dep-from,#5a9bf2), var(--btn-dep-to,#3d7fe0)); box-shadow: 0 2px 6px rgba(0,0,0,0.28); }
          .hdr-dw.wd  { background: linear-gradient(180deg, var(--btn-wd-from,#f78da2),  var(--btn-wd-to,#ef6885));  box-shadow: 0 2px 6px rgba(0,0,0,0.28); }
          .hdr-dw.fd  { background: linear-gradient(180deg, var(--btn-fd-from,#5a9bf2),  var(--btn-fd-to,#3d7fe0));  box-shadow: 0 2px 6px rgba(0,0,0,0.28); }
        }
      `}</style>

      <div className="hdr">

        {/* ROW 1 */}
        <div className="hdr-r1">

          {/* LEFT */}
          <div className="hdr-left">
            {isHomePage ? (
              // home icon redirects to home page (sidebar open disabled)
              <button className="hdr-ibtn" onClick={() => navigate('/home')} aria-label="Home">
                <Icons.Home style={{ fontSize: 22 }} />
              </button>
            ) : (
              <button className="hdr-ibtn" onClick={() => navigate('/home')} aria-label="Home">
                <Icons.Home style={{ fontSize: 22 }} />
              </button>
            )}
            <Link to="/home" className="hdr-logo">
              <img src={logo} alt={brand.name} />
            </Link>
          </div>

          {/* CENTER – desktop */}
          <div className="hdr-center">
            <div style={{ width: 300, flexShrink: 0 }}>
              <SearchDropdown variant="compact" />
            </div>
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Marquee />
            </div>
          </div>

          {/* RIGHT */}
          <div className="hdr-right">
            {showDeposit && (
              <button className="hdr-dw dep" onClick={() => navigate('/user/deposit')}>
                <FaPlusCircle size={12} />
                Deposit
              </button>
            )}
            {showWithdraw && (
              <button className="hdr-dw wd" onClick={() => navigate('/user/deposit-withdraw')}>
                <FaMoneyBillWave size={12} />
                Withdraw
              </button>
            )}
            {showFd && (
              <button className="hdr-dw fd" onClick={() => navigate('/user/fd')}>
                <FaPiggyBank size={12} />
                FD
              </button>
            )}

            <button className="hdr-rules" onClick={() => dispatch(setRulesModalOpen(true))}>
              <FaBookOpen size={12} />
              Rules
            </button>

            <div className="hdr-acct">
              {showBalance && (
                <div className="hdr-bal">
                  Balance:<b>{formatNumber(mainBalance)}</b>
                </div>
              )}

              <div className="hdr-exp-row">
                {showExposure && (
                  <span className="hdr-exp">
                    Exp:<b>{exposure ?? 0}</b>
                  </span>
                )}

                <UserDropdown
                  isDemoUser={isDemoUser}
                  showBalance={showBalance}
                  showExposure={showExposure}
                  handleBalanceChange={setShowBalance}
                  handleExposureChange={setShowExposure}
                  username={username}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ROW 2 – mobile search + marquee */}
        <div className="hdr-r2">
          <div className="hdr-r2-search">
            <SearchDropdown variant="compact" />
          </div>
          <div className="hdr-r2-marq">
            <Marquee type="mobile" />
          </div>
        </div>

        {/* MOBILE Deposit / Withdraw quick row */}
        {(showDeposit || showWithdraw || showFd) && (
          <div className="hdr-dwm">
            {showDeposit && (
              <button className="hdr-dwm-btn dep" onClick={() => navigate('/user/deposit')}>
                <FaPlusCircle size={13} />
                Deposit
              </button>
            )}
            {showWithdraw && (
              <button className="hdr-dwm-btn wd" onClick={() => navigate('/user/deposit-withdraw')}>
                <FaMoneyBillWave size={13} />
                Withdraw
              </button>
            )}
            {showFd && (
              <button className="hdr-dwm-btn fd" onClick={() => navigate('/user/fd')}>
                <FaPiggyBank size={13} />
                FD
              </button>
            )}
          </div>
        )}

        {/* BOTTOM NAV — hidden on report, crash, fantasy & game-details pages */}
        {!reportsPage && !isCrashPage && !isFantasyPage && !isGameDetailsPage && (
          <div className="hdr-bot">
            {!isQtechPage && !isFantasyPage && !isLiveCasinoPage && !isSlotsPage && !isCasinoPage && <HeaderTopGames />}
            <MobileNavbar />
            <Navbar />
          </div>
        )}

        <SetButtonValueModal />
        <RulesModal />
        <AgeRestrictionModal open={ageModalOpen} onClose={() => dispatch(setAgeModalOpen(false))} />
        <MyMarketModal open={showMyMarketModal} onClose={() => setShowMyMarketModal(false)} />
      </div>
    </>
  );
};

export default Header;
